import moment from 'moment';
import {
  sortChannelMap,
  streamingTableDataKeys,
  tableDataKeys,
} from '../constants';
import {
  addWithoutItem,
  dealwithNewOldData,
  type ChartItemType,
  dateTypeMap,
  getTimeSpaceArray,
  getAllNameList,
  pressTimeGrouping,
  arrayToObj,
  arrayToSimpleObj,
  getRationPercentValue,
  supplementSingleData,
  handleMentionData,
} from '@/pages/opinion/utils';
import type { ParamsTimeType } from '@/pages/opinion/types/common';
import { paramsToMomentMap } from '@/pages/opinion/utils/time';
import { dealwithBiLiBiLiImage } from '@op/utils/cdnUrl';

export const getPlatform = (objList: any, platform: string) => {
  //TODO:添加参数 寻找哪个字段 代替name
  if (typeof objList !== 'object') {
    return null;
  }
  let keys = Object.keys(objList);
  let idx = keys.indexOf(platform);
  if (idx < 0) {
    return null;
  } else {
    let metric = objList[platform]?.metric;
    if (!Array.isArray(metric) || !metric?.length) {
      return null;
    }
    return objList[platform]?.metric[0];
  }
};

/**默认排序 */
export function _sorter(firstVal: any, secondVal: any) {
  //fix: - 情况放最后  -999是有环比情况
  if (!firstVal || isNaN(parseInt(firstVal))) {
    firstVal = -99999;
  }
  if (!secondVal || isNaN(parseInt(secondVal))) {
    secondVal = -99999;
  }
  if (parseFloat(firstVal) > parseFloat(secondVal)) return 1;
  if (parseFloat(firstVal) < parseFloat(secondVal)) return -1;
  return 0;
}

export function dealwithChannelShareChartData(
  total: number,
  otherList: Array<any>,
  topIndex = 5,
) {
  if (otherList.length === 0) {
    return [];
  }
  let topValue = 0;
  let pieData: Array<any> = [];
  otherList.some((element, index) => {
    topValue += element.new;
    pieData.push({ name: element.user_name, value: element.new, ...element });
    return index > topIndex - 2;
  });

  const otherValue = total - topValue;
  pieData.push({ name: 'Others', value: otherValue });

  return pieData;
}

//排序
export const moveZero = (arr: Array<any>, inverse = false) => {
  if (!Array.isArray(arr)) {
    return [arr];
  }
  const valueArr: Array<any> = [];
  const underArr: Array<any> = [];

  arr?.forEach((item: any) => {
    if (
      (Array.isArray(item?.data) && item?.data[1] && !isNaN(item?.data[1])) ||
      (item?.data && !isNaN(item?.data))
    ) {
      valueArr.push(item);
    } else {
      underArr.push(item);
    }
  });
  const sortArr = valueArr.sort((a, b) => {
    if (typeof a.data === 'number') {
      return inverse ? a.data - b.data : b.data - a.data;
    }
    return inverse ? a.data[1] - b.data[1] : b.data[1] - a.data[1];
  });
  return [...sortArr, ...underArr];
};

function dealwithToTableData(
  timeGroup: { [key: string]: Array<ChartItemType> },
  keys: Array<string>,
) {
  const tableData: Array<{ [key: string]: any }> = [];
  for (const time in timeGroup) {
    const element = timeGroup[time];
    let tableItem: any = {
      date: time,
    };
    // 初始化
    keys.forEach((key) => {
      tableItem[key + '_count'] = 0;
      tableItem[key + '_count_than'] = 0;
    });
    // 修改值
    element.forEach(({ name, count, count_than }) => {
      tableItem[name + '_count'] = count;
      tableItem[name + '_count_than'] = count_than;
    });
    tableData.push(tableItem);
  }
  return tableData;
}

/**
 * 计算平均值
 * 计算规则：
 *    计算各个平台时间的平均数：
 *         1 - 2 - 3 - 4 天数据
 *         假如4号没有数据
 *         那么就是  (1 + 2 + 3) / 3天
 *
 *    上面平台数据平均数相加，开始平均平台
 *
 *    计算最终平均数
 *         平台A，平台B，平台C 数据
 *         假如平台A没有数据
 *         那么 (平台A + 平台B + 平台C) / 2个平台
 *
 * @param list  表格列表数据
 * @param days  日期跨度
 * @returns
 */
function countAvgData(list: Array<any>, days: number) {
  const daysObj: any = {
    twitch: {
      total: 0,
      days,
    },
    ytg: {
      total: 0,
      days,
    },
    facebook: {
      total: 0,
      days,
    },
  };
  const keys = Object.keys(daysObj);
  list.forEach((item) => {
    keys.forEach((key) => {
      const newKey = key + '_count';
      if (item[newKey] === 0) {
        daysObj[key].days--;
      } else {
        daysObj[key].total += item[newKey];
      }
    });
  });

  let avg = 0;
  let channelTotal = 0;
  keys.forEach((key) => {
    let { total, days } = daysObj[key];
    if (days !== 0) {
      avg += total / days;
      channelTotal++;
    }
  });

  return avg / channelTotal;
}

const funMap: any = {
  max: (cur: any, pre: any) => (cur > pre.count ? cur : pre.count),
  total: (cur: any, pre: any) => cur + pre.count,
};

function getTotalInfo(
  dataType: string,
  chartsData: any,
  tableData: any,
  newTimeGroupInfo: { [key: string]: Array<any> },
  dateArr: any,
) {
  let total = 0;
  if (dataType === 'avg') {
    total = countAvgData(tableData, dateArr.length);
  } else if (dataType === 'peakCCvMax') {
    let maxValueList = Object.values(newTimeGroupInfo || {}).map((item) =>
      item.reduce(funMap.total, 0),
    );
    total = Math.max(...maxValueList);
  } else {
    try {
      total = chartsData.reduce(funMap[dataType || 'max'], 0);
    } catch (error) {}
  }

  return total;
}

/**
 * 处理图表，表格数据的地方
 * 记住：基础数据会多一天，所以不能使用基础数据去求和等
 * @param start_time     开始时间
 * @param end_time       结束时间
 * @param basic_data     基础列表数据
 * @param date_type      时间类型，涉及到时间分段
 * @param dataType       数据类型，看求和，还是求平均数，还是求最大值
 * @param allKey         所有的key，加入数据里缺少数据就会使用这个处理
 * @returns
 */
export function dealwithRatio(
  start_time: string,
  end_time: string,
  basic_data: Array<ChartItemType>,
  date_type: ParamsTimeType,
  dataType?: 'max' | 'avg' | 'total' | 'peakCCvMax',
  allKey?: Array<string>,
) {
  let dateType = dateTypeMap[date_type];
  let format = dateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
  const dateArr = getTimeSpaceArray(start_time, end_time, dateType, format);
  const allKeyList = allKey || getAllNameList(basic_data);
  const timeGroup: any = pressTimeGrouping(basic_data, format);

  const newTimeGroupInfo: any = {};
  for (let index = 0; index < dateArr.length; index++) {
    let newKey = dateArr[index];
    let oldKey = dateArr[index - 1];
    if (index === 0) {
      try {
        // 将timeGroup的key排序后取第一个
        oldKey = Object.keys(timeGroup).sort((a, b) =>
          Number(moment(a).isBefore(moment(b))),
        )[0];
      } catch (error) {
        // 如果有问题就走这个备用方案，直接当前key - 1天
        oldKey = moment(newKey)
          .subtract(1, paramsToMomentMap[date_type])
          .format(format);
      }
    }
    newTimeGroupInfo[newKey] = dealwithNewOldData(
      timeGroup[newKey],
      timeGroup[oldKey],
      'name',
      ['count'],
    );
  }
  const tableData = dealwithToTableData(newTimeGroupInfo, allKeyList);
  const chartsData = addWithoutItem(newTimeGroupInfo, allKeyList, dateArr);
  let total = 0;
  if (dataType) {
    total = getTotalInfo(
      dataType,
      chartsData,
      tableData,
      newTimeGroupInfo,
      dateArr,
    );
  }

  return {
    table: tableData,
    charts: chartsData,
    total,
    timeGroupInfo: newTimeGroupInfo,
  };
}

/**
 * 按上面的顺序排序
 * @param list     排序数据
 * @param sortFun  返回一个方法，并带有两个key
 */
export function sortChannel(
  list: Array<any>,
  sortFun = (func: any) => (a: any, b: any) => func(a, b),
) {
  list.sort(sortFun((a: any, b: any) => sortChannelMap[a] - sortChannelMap[b]));
}

export function dealwithInfluenceTableData<T>(
  new_list: Array<any>,
  old_list: Array<any>,
  old_plus_list: Array<any>,
  user_info_list: Array<any>,
  max_item: any,
  media_type: any,
) {
  const key = media_type === 'streaming' ? 'user_id' : 'channel_user_name';
  const userObj = arrayToObj(user_info_list, key);
  const oldPlusObj = arrayToSimpleObj(old_plus_list, 'followers', key);
  const keys =
    media_type === 'streaming' ? streamingTableDataKeys : tableDataKeys;
  return dealwithNewOldData(
    new_list,
    old_list,
    key,
    keys,
    (item: any, oldItem: any) => {
      let { followers } = item;
      const newKey = item[key];
      const otherInfo = userObj[newKey] || {};
      const beforeValue = oldPlusObj[newKey];
      const oldValue = oldItem?.followers || 0;
      let followers_grow = followers - oldValue;
      let old_followers_grow = oldValue - beforeValue;
      otherInfo.followers_grow_than = getRationPercentValue(
        followers_grow,
        old_followers_grow,
      );
      otherInfo.followers_grow = followers_grow;
      otherInfo.old_followers_grow = old_followers_grow || 0;
      keys.map((key) => {
        otherInfo['old_' + key] = oldItem?.[key] || 0;
      });

      return otherInfo;
    },
    max_item,
  );
}

const dealwithInfluencersLineData = (
  start_time: string,
  end_time: string,
  list: Array<any>,
) => {
  // 转为时间为key的对象
  const dateListObj = arrayToObj(list, (item) =>
    moment(item.time).format('YYYY-MM-DD'),
  );
  // 获取时间数组
  const dateArr = getTimeSpaceArray(start_time, end_time);

  const lineSeriesData: Array<{ name: string; data: Array<number> }> = [],
    lineXAxisData: Array<string> = [],
    data: Array<number> = [];

  dateArr.forEach((time) => {
    const obj = dateListObj[time] || { value: 0 };
    lineXAxisData.push(time);
    data.push(obj.value);
  });

  lineSeriesData.push({
    name: 'Influencers',
    data: data,
  });

  return [lineSeriesData, lineXAxisData];
};

const dealwithOtherLineData = (
  start_time: string,
  end_time: string,
  list: Array<any>,
  allKey: Array<string>,
) => {
  const chartsData = supplementSingleData(
    start_time,
    end_time,
    list,
    'daily',
    allKey,
  );
  let { newArr, timeArr } = handleMentionData(chartsData, 'days');
  return [newArr, timeArr];
};

export function dealwithKeyMetricsLine(
  start_time: string,
  end_time: string,
  list: Array<any>,
  type: string,
  allKey: Array<string>,
) {
  if (!list || list.length === 0) {
    return [[], []];
  }
  if (type === 'kolNumber') {
    return dealwithInfluencersLineData(start_time, end_time, list);
  } else {
    return dealwithOtherLineData(start_time, end_time, list, allKey);
  }
}

export function dealwithVideoJSON(detail: string) {
  try {
    let obj = JSON.parse(detail);
    // 判断是否是B站图片
    obj.cover_image = dealwithBiLiBiLiImage(obj.cover_image);
    return obj;
  } catch (error) {
    return {};
  }
}
